import { FC } from 'react';
import { Box } from '@mui/system';
import { ProCrownIcon } from '../../icons/ProCrownIcon';
import { ProBadgeTooltip } from './pro-badge-tooltip.component';

interface ProBadgeProps {
	withCrown?: boolean;
	size?: 'small' | 'medium' | 'large';
}

const SIZE_CONFIG = {
	small: {
		fontSize: '12px',
		crownSize: '14px',
		gap: '2px',
		padding: '1px 5px',
	},
	medium: {
		fontSize: '14px',
		crownSize: '18px',
		gap: '4px',
		padding: '3px 7px',
	},
	large: {
		fontSize: '17px',
		crownSize: '24px',
		gap: '8px',
		padding: '7px 11px',
	},
};

export const ProBadge: FC<ProBadgeProps> = ({ withCrown, size = 'medium' }) => {
	const config = SIZE_CONFIG[size];

	return (
		<ProBadgeTooltip>
			<Box
				sx={{
					display: 'inline-flex',
					borderRadius: '100px',
					position: 'relative',
					padding: '1px',
					background:
						'conic-gradient(from 30deg, rgb(255, 205, 78) 0%, rgb(253, 246, 125) 20%, rgb(253, 246, 125) 40%, rgb(247, 195, 77) 60%, rgb(255, 255, 255) 74%, rgb(253, 186, 61) 100%)',
					boxShadow: '0px 7px 32px 0px rgba(219, 142, 0, 0.40), 0px 4px 61px 0px rgba(219, 142, 0, 0.50)',
					overflow: 'hidden',

					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						width: '40%',
						height: '100%',
						background:
							'linear-gradient(120deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.0) 100%)',
						transform: 'translateX(-110%) skewX(-20deg)',
						zIndex: 1,
					},

					'&:hover': {
						'&::before': {
							animation: 'shine 0.8s linear',
						},
					},

					'@keyframes shine': {
						'0%': { transform: 'translateX(-110%) skewX(-20deg)' },
						'100%': { transform: 'translateX(260%) skewX(-20deg)' },
					},
				}}
			>
				<Box
					sx={{
						padding: config.padding,
						background:
							'linear-gradient(110deg, rgb(255 202 75) 11%, rgb(255 251 142) 27%, rgb(250 198 75) 57%, rgb(254 230 110) 75%, rgb(245 189 71) 100%)',
						borderRadius: 500,
						justifyContent: 'flex-start',
						alignItems: 'center',
						gap: config.gap,
						display: 'flex',
					}}
				>
					{withCrown && <ProCrownIcon width={config.crownSize} height={config.crownSize} />}

					<Box
						sx={{
							justifyContent: 'center',
							display: 'flex',
							flexDirection: 'column',
							fontSize: config.fontSize,
							fontFamily: 'Montserrat, sans-serif',
							fontWeight: '800',
							position: 'relative',
							zIndex: 2,

							'&::after': {
								content: "'PRO'",
								display: 'block',
								width: '100%',
								height: '1px',
								position: 'absolute',
								color: '#fff3ca',
								top: '0.6px',
								left: '0.6px',
								zIndex: '-1',
							},
						}}
					>
						<Box
							sx={{
								background: 'linear-gradient(180deg, #866100 20%, #feb602 90%)',
								backgroundClip: 'text',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}
						>
							PRO
						</Box>
					</Box>
				</Box>
			</Box>
		</ProBadgeTooltip>
	);
};
